import { useEffect, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut } from "lucide-react";

export function ImageLightbox({ src, alt = "", onClose }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const pinch = useRef(null);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  if (!src) return null;
  const clamp = (s) => Math.min(5, Math.max(1, s));
  const zoom = (d) => setScale((s) => { const n = clamp(s + d); if (n === 1) setPos({ x: 0, y: 0 }); return n; });
  const onWheel = (e) => { e.preventDefault(); zoom(e.deltaY < 0 ? 0.3 : -0.3); };
  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) pinch.current = { d: dist(e.touches), s: scale };
    else if (e.touches.length === 1 && scale > 1) drag.current = { x: e.touches[0].clientX - pos.x, y: e.touches[0].clientY - pos.y };
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinch.current) { e.preventDefault(); setScale(clamp(pinch.current.s * (dist(e.touches) / pinch.current.d))); }
    else if (e.touches.length === 1 && drag.current) { e.preventDefault(); setPos({ x: e.touches[0].clientX - drag.current.x, y: e.touches[0].clientY - drag.current.y }); }
  };
  const onTouchEnd = () => { pinch.current = null; drag.current = null; if (scale <= 1) setPos({ x: 0, y: 0 }); };
  const onMouseDown = (e) => { if (scale > 1) drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }; };
  const onMouseMove = (e) => { if (drag.current) setPos({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }); };
  const onMouseUp = () => { drag.current = null; };
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center select-none" onClick={onClose} onWheel={onWheel}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
      data-testid="image-lightbox">
      <button type="button" onClick={onClose} className="absolute top-3 right-3 z-10 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center" aria-label="Tutup" data-testid="lightbox-close"><X className="w-6 h-6" /></button>
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 rounded-full bg-white/15 px-2 py-1 text-white" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => zoom(-0.5)} className="w-9 h-9 rounded-full hover:bg-white/20 flex items-center justify-center" aria-label="Perkecil" data-testid="lightbox-zoom-out"><ZoomOut className="w-5 h-5" /></button>
        <span className="text-xs font-mono w-12 text-center" data-testid="lightbox-scale">{Math.round(scale * 100)}%</span>
        <button type="button" onClick={() => zoom(0.5)} className="w-9 h-9 rounded-full hover:bg-white/20 flex items-center justify-center" aria-label="Perbesar" data-testid="lightbox-zoom-in"><ZoomIn className="w-5 h-5" /></button>
      </div>
      <img src={src} alt={alt} draggable={false} onClick={(e) => e.stopPropagation()} onDoubleClick={() => (scale > 1 ? (setScale(1), setPos({ x: 0, y: 0 })) : setScale(2.5))}
        className={`max-w-[95vw] max-h-[90vh] object-contain transition-transform duration-75 ${scale > 1 ? "cursor-grab" : "cursor-zoom-in"}`}
        style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})` }} data-testid="lightbox-img" />
    </div>
  );
}
